import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Instagram } from 'lucide-react';
import api from '../services/api';
import { isCurrentUserAdmin } from '../utils/adminCheck';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import SponsorsSection from '../components/SponsorsSection';
import ScrollToTop from '../components/ScrollToTop';
import TornPaperWrapper from '../components/TornPaperWrapper';
import { getCmsImagePath } from '../cms/cmsData';
import './MohammedAssaf.css';

const TikTokIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.8a8.24 8.24 0 0 0 4.76 1.52V6.87a4.84 4.84 0 0 1-1-.18z"/>
  </svg>
);

const SpotifyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_1021_27499)">
      <path d="M16 1.71429V14.2857C16 15.2321 15.2321 16 14.2857 16H11.2393V9.67143H13.4036L13.7143 7.25714H11.2393V5.71429C11.2393 5.01429 11.4321 4.53929 12.4357 4.53929H13.7143V2.38214C13.4929 2.35357 12.7357 2.28571 11.85 2.28571C10.0071 2.28571 8.74286 3.41071 8.74286 5.47857V7.26071H6.57143V9.675H8.74643V16H1.71429C0.767857 16 0 15.2321 0 14.2857V1.71429C0 0.767857 0.767857 0 1.71429 0H14.2857C15.2321 0 16 0.767857 16 1.71429Z" fill="#1D1D1D"/>
    </g>
    <defs>
      <clipPath id="clip0_1021_27499">
        <rect width="16" height="16" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.5997 0.768311H15.0543L9.69318 6.89434L16 15.2316H11.063L7.19339 10.1764L2.77097 15.2316H0.312907L6.04607 8.67791L0 0.768311H5.06215L8.55628 5.38891L12.5997 0.768311ZM11.7375 13.7644H13.0969L4.3216 2.15901H2.86136L11.7375 13.7644Z" fill="#1D1D1D"/>
  </svg>
);

const YoutubeIcon = () => (
  <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.6661 1.76053C15.4815 1.06628 14.9396 0.521421 14.2512 0.336873C13.0033 0 8 0 8 0C8 0 2.9967 0 1.74881 0.336873C1.06042 0.521421 0.518491 1.06628 0.333944 1.76053C0 3.01721 0 5.63603 0 5.63603C0 5.63603 0 8.25485 0.333944 9.51154C0.518491 10.2058 1.06042 10.7272 1.74881 10.9118C2.9967 11.2486 8 11.2486 8 11.2486C8 11.2486 13.0033 11.2486 14.2512 10.9118C14.9396 10.7272 15.4815 10.2029 15.6661 9.51154C16 8.25485 16 5.63603 16 5.63603C16 5.63603 16 3.01721 15.6661 1.76053ZM6.36543 8.01465V3.25741L10.5456 5.63603L6.36543 8.01465Z" fill="#1D1D1D"/>
  </svg>
);

const SnapchatIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M15.9543 11.6474C15.8422 11.3439 15.6311 11.1823 15.3903 11.0471C15.3441 11.0207 15.3046 10.9976 15.2683 10.9844C15.1957 10.9481 15.1232 10.9119 15.0506 10.8723C14.2986 10.4732 13.7116 9.9719 13.3026 9.37494C13.1872 9.20673 13.0849 9.02863 13.0025 8.84394C12.9662 8.74499 12.9695 8.68892 12.9959 8.63615C13.0223 8.59657 13.0519 8.56359 13.0915 8.53721C13.2201 8.45146 13.3554 8.3657 13.4444 8.30634C13.606 8.2008 13.7346 8.11834 13.8138 8.06228C14.1238 7.8479 14.3382 7.61703 14.4734 7.35977C14.5691 7.18167 14.6218 6.98708 14.635 6.7859C14.6482 6.58471 14.6153 6.38352 14.5427 6.19883C14.3382 5.66123 13.8303 5.32812 13.2135 5.32812C13.0849 5.32812 12.953 5.34131 12.8277 5.3677C12.7947 5.37429 12.7584 5.38419 12.7254 5.39078C12.732 5.02139 12.7221 4.63551 12.6891 4.25293C12.5737 2.90729 12.1021 2.20478 11.6106 1.6408C11.2973 1.2879 10.9279 0.99107 10.5157 0.7602C9.77029 0.33474 8.92597 0.120361 8.00578 0.120361C7.0856 0.120361 6.24458 0.33474 5.4992 0.7602C5.08693 0.99107 4.71754 1.2912 4.40092 1.6441C3.9095 2.20478 3.43786 2.91059 3.32243 4.25623C3.28945 4.63881 3.28285 5.02799 3.28615 5.39408C3.25317 5.38419 3.22019 5.37759 3.18391 5.371C3.05528 5.34461 2.92665 5.33142 2.79803 5.33142C2.18127 5.33142 1.67336 5.66453 1.46888 6.20213C1.39632 6.39012 1.36334 6.59131 1.37653 6.7892C1.38972 6.98708 1.44249 7.18497 1.53814 7.36307C1.67336 7.62033 1.89104 7.8479 2.19776 8.06557C2.28022 8.12164 2.40885 8.2041 2.56716 8.30964C2.65291 8.3657 2.78153 8.44816 2.90686 8.53061C2.94974 8.5603 2.98602 8.59657 3.0157 8.63945C3.04209 8.69222 3.04209 8.75159 3.00251 8.85713C2.92006 9.03852 2.82111 9.21333 2.70898 9.37823C2.3099 9.962 1.73932 10.4534 1.01373 10.8492C0.627851 11.0537 0.225479 11.1889 0.0572736 11.6507C-0.0713538 11.997 0.0143978 12.3927 0.337615 12.7258C0.456348 12.8512 0.59487 12.9534 0.746584 13.0359C1.06321 13.2107 1.39962 13.3426 1.74592 13.4349C1.81848 13.4547 1.88774 13.4844 1.94711 13.524C2.06584 13.6262 2.04935 13.7845 2.20436 14.0121C2.28352 14.1308 2.38246 14.2331 2.50119 14.3122C2.83101 14.5398 3.2037 14.5563 3.59617 14.5695C3.95237 14.5827 4.35475 14.5992 4.81319 14.7509C5.00448 14.8136 5.20237 14.9356 5.42994 15.0774C5.98073 15.4171 6.736 15.8789 7.99589 15.8789C9.25578 15.8789 10.0176 15.4138 10.5717 15.0741C10.7993 14.9356 10.9972 14.8136 11.1819 14.7509C11.6403 14.5992 12.046 14.5827 12.3989 14.5695C12.7914 14.553 13.1641 14.5398 13.4939 14.3122C13.6324 14.2166 13.7478 14.0913 13.8303 13.9429C13.9424 13.7516 13.9424 13.6163 14.048 13.5207C14.1073 13.4811 14.17 13.4514 14.2393 13.4349C14.5922 13.3426 14.9319 13.2074 15.2551 13.0326C15.4167 12.9468 15.5618 12.8314 15.6838 12.6962L15.6871 12.6896C15.9906 12.363 16.0664 11.9805 15.9444 11.6408L15.9543 11.6474Z" 
      fill="white" 
      stroke="#1D1D1D" 
      strokeWidth="0.5"
    />
  </svg>
);

const videos = [
  { id: 'z-Oa73xynuc', title: 'Falasteen Enty El Rouh - Mohammed Assaf', views: '14M+ Views' },
  { id: 'czT_SIjZN_Q', title: 'Dehiyya - Rawabi concert', views: '11M+ Views' },
  { id: 'q_Q9PgQrKtA', title: 'Jinalek Ya Falsteen', views: '5.6M+ Views' },
  { id: 'f3im9rA38Z8', title: 'Ya Banat Bladna', views: '3.6M+ Views' },
  { id: 'wG43Alc5C-A', title: 'Belhob Mnou2a3', views: '3.2M+ Views' },
  { id: '4CSokyrdVgE', title: 'Salam Le Gaza', views: '3.1M+ Views' },
];

const MohammedAssaf = () => {
  const [bioExpanded, setBioExpanded] = useState(false);
  const [allowed, setAllowed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await api.getPublicConfig();
        if ((response?.success && response.data?.amphitheater_sunday_enabled) || isCurrentUserAdmin()) {
          setAllowed(true);
        } else {
          navigate('/music', { replace: true });
        }
      } catch {
        navigate('/music', { replace: true });
      }
    };
    checkAccess();
  }, [navigate]);

  if (!allowed) return null;

  return (
    <div className="page-wrapper">
      <AnnouncementBar />
      
      <section className="hero-section">
        <div className="hero-background-wrapper">
          <video src={"/hero-final.mp4"} alt="oc mena festival" className="hero-background-video" autoPlay muted loop playsInline />
          <div className="hero-gradient-overlay"></div>
        </div>

        <TornPaperWrapper>
          {/* ===== TOP HEADER ===== */}
          <div className="assaf-top-header">
            <div className="assaf-top-header-left">
              <h1>Mohammed Assaf at the OC MENA Festival!</h1>
              <p>Performing Sunday, June 21, 2026</p>
            </div>
            <a href="/amphitheater-tickets-sunday" className="assaf-buy-btn" title="Buy Tickets">Buy Tickets</a>
          </div>

          {/* ===== SPLIT LAYOUT ===== */}
          <div className="assaf-split-layout">
            {/* LEFT COLUMN: Photo, Flag, Details */}
            <div className="assaf-left-col">
              <img loading="lazy" src={getCmsImagePath('assafPortrait', '/assaf.webp')} alt="Mohammed Assaf" className="assaf-portrait" />
              <img loading="lazy" src={getCmsImagePath('assafFlag', '/flag_2.webp')} alt="Palestinian Flag" className="assaf-flag-img" />

              <div className="assaf-details">
                <h3>Details</h3>
                <div className="assaf-details-label">Background</div>
                <div className="assaf-details-value">Palestinian</div>
                <div className="assaf-details-label">Socials</div>
                <ul className="assaf-socials-list">
                  <li>
                    <a href="https://www.instagram.com/mohammedassaf89" target="_blank" rel="noopener noreferrer" title="Mohammed Assaf on Instagram">
                      <Instagram size={18} />
                      Instagram <span className="assaf-social-count">(5.7M+)</span>
                    </a>
                  </li>
                  <li>
                    <a href="https://www.tiktok.com/@mohammedassafofficial" target="_blank" rel="noopener noreferrer" title="Mohammed Assaf on TikTok">
                      <TikTokIcon />
                      TikTok <span className="assaf-social-count">(1.4M+ Likes)</span>
                    </a>
                  </li>
                  <li>
                    <a href="https://open.spotify.com/artist/0IjIdnhlsKfAfOl5ph5TsE" target="_blank" rel="noopener noreferrer" title="Mohammed Assaf on Spotify">
                      <SpotifyIcon />
                      Spotify
                    </a>
                  </li>
                  <li>
                    <a href="https://www.facebook.com/MohammedAssafOfficial" target="_blank" rel="noopener noreferrer" title="Mohammed Assaf on Facebook">
                      <FacebookIcon />
                      Facebook <span className="assaf-social-count">(12M+)</span>
                    </a>
                  </li>
                  <li>
                    <a href="https://x.com/MohammedAssaf89" target="_blank" rel="noopener noreferrer" title="Mohammed Assaf on X">
                      <XIcon />
                      X (Twitter) <span className="assaf-social-count">(2.6M+)</span>
                    </a>
                  </li>
                  <li>
                    <a href="https://www.youtube.com/@MohammedAssafOfficial" target="_blank" rel="noopener noreferrer" title="Mohammed Assaf on YouTube">
                      <YoutubeIcon />
                      YouTube <span className="assaf-social-count">(80M+ views)</span>
                    </a>
                  </li>
                  <li>
                    <a href="https://www.snapchat.com/add/mohammedassaf" target="_blank" rel="noopener noreferrer" title="Mohammed Assaf on Snapchat">
                      <SnapchatIcon />
                      Snapchat
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* RIGHT COLUMN: Name logo, Bio */}
            <div className="assaf-right-col">
              <div className="assaf-name-img">
                <img loading="lazy" src={getCmsImagePath('assafName', '/name.webp')} alt="Mohammed Assaf" />
              </div>

              <p className="assaf-billboard-subtitle">
                <strong>Winner of Arab Idol</strong>
              </p>

              <h2 className="assaf-bio-heading">Bio</h2>
              <div className={`assaf-bio-container${bioExpanded ? ' expanded' : ''}`}>
                <p>
                  <strong>Mohammed Assaf</strong> is a Palestinian pop singer whose rise to fame became a defining cultural moment across the Arab world. He first captured international attention after winning Arab Idol, a victory that drew unprecedented public support and celebration throughout Palestine, the wider Middle East, and the global Arab diaspora. His voice, stage presence, and emotional connection to audiences quickly set him apart, turning a televised competition into the launch of a lasting career.
                </p>
                <p>
                  Born to Palestinian parents and raised in Gaza, Assaf's early life was shaped by displacement, resilience, and a deep connection to music. He began singing at a young age, performing at weddings and local events without formal vocal training. Even before his breakthrough, his talent was widely recognized, earning him early television appearances and performances at major cultural gatherings. Music became both his outlet and his path forward.
                </p>
                <p>
                  Assaf's journey to Arab Idol is now legendary. Facing travel restrictions and closed auditions, he persisted against overwhelming odds, ultimately earning a chance to perform and winning over both the judges and millions of viewers. Nicknamed "The Rocket" for his rapid rise, his performances blended classic Arabic songs with modern emotion, drawing comparisons to legendary vocalists and solidifying his reputation as a once-in-a-generation talent.
                </p>
                <p>
                  Following his win, Mohammed Assaf's career expanded rapidly. He released multiple albums and hit singles, many earning gold and platinum certifications, and toured extensively across the Middle East, Europe, North America, and beyond. His music spans romantic ballads, patriotic anthems, and cross-genre collaborations, sung across multiple Arabic dialects and styles that reflect the diversity of his audience.
                </p>
                <p>
                  Today, Mohammed Assaf stands as one of the most influential Arab artists of his generation. Beyond commercial success, he is widely regarded as a cultural symbol — uniting audiences through music that speaks to heritage, identity, and shared experience. Whether performing on major international stages or connecting with fans through deeply personal songs, Assaf continues to represent the power of music to transcend borders and bring people together.
                </p>
                {!bioExpanded && <div className="assaf-bio-fade"></div>}
              </div>
              <button className="assaf-read-more" onClick={() => setBioExpanded(!bioExpanded)}>
                {bioExpanded ? 'Read less' : 'Read More'}
              </button>

              {/* ===== BUY TICKETS CENTER ===== */}
              <div className="assaf-center-btn">
                <a href="/amphitheater-tickets-sunday" className="assaf-buy-btn" title="Buy Tickets">Buy Tickets</a>
              </div>

              {/* ===== YOUTUBE x MUSIC ===== */}
              <h2 className="assaf-youtube-heading">YouTube x Music</h2>
              <div className="assaf-videos-grid">
                {videos.slice(0, 4).map((video) => (
                  <div className="assaf-video-card" key={video.id}>
                    <div className="video-embed-wrapper">
                      <iframe
                        src={`https://www.youtube.com/embed/${video.id}`}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                    <div className="assaf-video-title">{video.title}</div>
                    <div className="assaf-video-views">{video.views}</div>
                  </div>
                ))}
              </div>
              
              {/* First 4 videos */}
              <div className="assaf-videos-grid" style={{ marginTop: '0' }}>
                {videos.slice(4).map((video) => (
                  <div className="assaf-video-card" key={video.id}>
                    <div className="video-embed-wrapper">
                      <iframe
                        src={`https://www.youtube.com/embed/${video.id}`}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                    <div className="assaf-video-title">{video.title}</div>
                    <div className="assaf-video-views">{video.views}</div>
                  </div>
                ))}
              </div>
              
              {/* Buy Tickets Button Centered */}
              <div style={{ textAlign: 'center', margin: '30px 0' }}>
                <a href="/amphitheater-tickets-sunday" className="assaf-buy-btn" title="Buy Tickets">Buy Tickets</a>
              </div>

              {/* ===== ARAB IDOL SECTION ===== */}
              <div className="assaf-arab-idol">
                <h3>Arab Idol Winner</h3>
                <img loading="lazy" src={getCmsImagePath('arabIdolLogo', '/arab_idol.webp')} alt="Arab Idol" className="assaf-arab-idol-img" />
                <img loading="lazy" src={getCmsImagePath('arabIdolPerformance', '/arab_idol_2.webp')} alt="Arab Idol Performance" className="assaf-arab-idol-img" />
              </div>

              {/* ===== FINAL BUY TICKETS ===== */}
              <div className="assaf-center-btn" style={{ marginTop: '40px' }}>
                <a href="/amphitheater-tickets-sunday" className="assaf-buy-btn" title="Buy Tickets">Buy Tickets</a>
              </div>
            </div>
          </div>
        </TornPaperWrapper>

        <div className="lanterns-container">
          <img loading="lazy" src={getCmsImagePath('lanterns', '/lanterns.webp')} alt="festival lanterns" className="lanterns-image" />
        </div>
      </section>

      <SponsorsSection />
      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default MohammedAssaf;
